import { create } from 'zustand';
import { api } from '../services/api';

export type Rating = 'Yes' | 'No' | 'Consider';

export interface RatingCell {
  rating: Rating | null;
  notes?: string;
  updatedAt?: string;
}

// ratings[category][modelId] = cell
export type RatingsMap = Record<string, Record<string, RatingCell>>;

export interface CategorySummary {
  yes: string[];
  no: string[];
  consider: string[];
  needsReeval: boolean;
}

export type SummaryMap = Record<string, CategorySummary>;

// Model to rate — matches GET /api/config/models shape ({ id, displayName, ... }).
export interface RatingModel {
  id: string;
  displayName: string;
  provider: string;
  modelId: string;
  capabilities?: Record<string, boolean>;
}

export interface FlaggedCategory {
  category: string;
  yesModels: string[];
  yesCount: number;
}

interface RatingsState {
  ratings: RatingsMap;
  summary: SummaryMap;
  categories: string[];
  models: RatingModel[];
  flagged: FlaggedCategory[];
  reevalThreshold: number;
  loading: boolean;
  error: string | null;
  fetchRatings: () => Promise<void>;
  fetchReeval: () => Promise<void>;
  setRating: (
    category: string,
    model: string,
    rating: Rating | null,
    notes?: string
  ) => Promise<void>;
}

const DEFAULT_THRESHOLD = 3;

export const useRatingsStore = create<RatingsState>()((set, get) => ({
  ratings: {},
  summary: {},
  categories: [],
  models: [],
  flagged: [],
  reevalThreshold: DEFAULT_THRESHOLD,
  loading: false,
  error: null,

  fetchRatings: async () => {
    set({ loading: true, error: null });
    try {
      const [ratingsRes, modelsRes] = await Promise.all([
        api.get('/api/ratings') as Promise<{
          ratings?: RatingsMap;
          summary?: SummaryMap;
          categories?: string[];
          reevalThreshold?: number;
        }>,
        api.get('/api/config/models') as Promise<{ models?: RatingModel[] }>,
      ]);
      set({
        ratings: ratingsRes.ratings || {},
        summary: ratingsRes.summary || {},
        categories: ratingsRes.categories || [],
        reevalThreshold: ratingsRes.reevalThreshold ?? DEFAULT_THRESHOLD,
        models: modelsRes.models || [],
        loading: false,
      });
      await get().fetchReeval();
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
    }
  },

  fetchReeval: async () => {
    try {
      const data = await api.get('/api/ratings/reeval') as {
        flagged?: FlaggedCategory[];
        reevalThreshold?: number;
      };
      set({
        flagged: data.flagged || [],
        reevalThreshold: data.reevalThreshold ?? get().reevalThreshold,
      });
    } catch {
      set({ flagged: [] });
    }
  },

  setRating: async (category, model, rating, notes) => {
    const previous = get().ratings;
    // Optimistic update of the single cell.
    const nextCell: RatingCell = {
      rating,
      notes: notes ?? previous[category]?.[model]?.notes,
      updatedAt: new Date().toISOString(),
    };
    set({
      ratings: {
        ...previous,
        [category]: { ...previous[category], [model]: nextCell },
      },
    });
    try {
      const result = await api.put('/api/ratings', {
        category,
        model,
        rating,
        notes,
      }) as {
        ratings?: RatingsMap;
        summary?: SummaryMap;
        triggeredReeval?: boolean;
      };
      set({
        ratings: result.ratings || get().ratings,
        summary: result.summary || get().summary,
      });
      await get().fetchReeval();
    } catch (err) {
      set({ ratings: previous, error: (err as Error).message });
      throw err;
    }
  },
}));
